import {ControllerFlowAPI} from '@wix/yoshi-flow-editor';
import type {IWidgetControllerConfig} from '@wix/native-components-infra/dist/src/types/types';
import {createDeliverySectionController, DeliverySectionController} from '@wix/delivery-section-checkout/controller';
import {CheckoutService} from '../../services/checkout/CheckoutService';
import {DeliveryStoreProps} from '../../../types/checkoutApp.types';
import {DeliverySectionService} from '../../services/checkout/DeliverySectionService';
import {ComponentState} from '@wix/delivery-section-checkout/types';
import {SPECS} from '../../../common/constants';

export type DeliveryStoreConfig = {
  flowAPI: ControllerFlowAPI;
  controllerConfig: IWidgetControllerConfig;
  checkoutService: CheckoutService;
  updateComponent: () => void;
  deliverySectionService: DeliverySectionService;
};

export class DeliveryStore {
  private readonly flowApi: ControllerFlowAPI;
  private readonly controllerConfig: IWidgetControllerConfig;
  private deliverySectionController!: DeliverySectionController;
  private readonly checkoutService!: CheckoutService;
  private readonly updateComponent: () => void;
  private readonly deliverySectionService: DeliverySectionService;

  constructor({
    flowAPI,
    controllerConfig,
    checkoutService,
    updateComponent,
    deliverySectionService,
  }: DeliveryStoreConfig) {
    this.flowApi = flowAPI;
    this.controllerConfig = controllerConfig;
    this.checkoutService = checkoutService;
    this.updateComponent = updateComponent;
    this.deliverySectionService = deliverySectionService;
  }

  public readonly init = () => {
    this.setDeliveryController();
    void this.deliverySectionController.init();
  };

  private readonly updateComponentState = (state: ComponentState) => {
    this.deliverySectionService.updateComponentState(state);
    this.updateComponent();
  };

  private readonly setDeliveryMethodId = async (id: string) => {
    await this.checkoutService.setShippingOption(id);
    this.updateComponent();
  };

  private readonly setDeliveryController = () => {
    this.deliverySectionController = createDeliverySectionController({
      flowAPI: this.flowApi,
      controllerConfig: this.controllerConfig,
      getCheckoutV1: async () => {
        const parallelPromisesImprovement = this.flowApi.experiments.enabled(
          SPECS.ImprovePerformanceByParallelPromises
        );
        /* istanbul ignore else */
        if (parallelPromisesImprovement) {
          await this.checkoutService.init();
          return Promise.resolve(this.checkoutService.sdkCheckoutForDeliverySection!);
        } else {
          return Promise.resolve(this.checkoutService.sdkCheckoutForDeliverySection!);
        }
      },
      getCartV2: this.flowApi.experiments.enabled(SPECS.CallCartV2FromCheckout)
        ? async () => {
            await this.checkoutService.init();
            return Promise.resolve(this.checkoutService.sdkCartForDeliverySection!);
          }
        : undefined,
      setDeliveryMethodId: (id: string) => this.setDeliveryMethodId(id),
      onStateChanged: (state: ComponentState) => Promise.resolve(this.updateComponentState(state)),
      onRefreshCheckout:
        /* istanbul ignore next */
        () => void this.checkoutService.onRefreshCheckout(),
    });

    this.deliverySectionService.setCheckoutUpdateCallback(
      this.deliverySectionController.onCheckoutUpdated.bind(this.deliverySectionController)
    );
  };

  public toProps(): DeliveryStoreProps {
    return {
      hasSelectedOption: !!this.checkoutService.cart.deliveryInfo.method,
      componentState: this.deliverySectionService.componentState,
    };
  }
}
